import { Banner, BannerKind, Body, Button, Column, Row } from "@gadgetinc/widgets";
import { useStyletron } from "baseui";
import { last } from "lodash";
import { DateTime } from "luxon";
import { observer } from "mobx-react-lite";
import React, { useMemo } from "react";
import { BillingContract } from "state-trees/src/BillingContract";

export const PendingPaymentBanner = observer(
  (props: { billingContract: BillingContract; onClose?: () => void; onAddPaymentMethod?: () => void }) => {
    const { billingContract, onAddPaymentMethod } = props;
    const [_, $theme] = useStyletron();

    const pendingBankConfirmation = billingContract.pendingBankConfirmation;
    const pendingInvoices = billingContract.pendingInvoices;
    const hasPendingPayments = billingContract.hasPendingPayments;

    const kind = pendingInvoices.length > 0 ? BannerKind.negative : BannerKind.warning;

    const dismissable = kind === BannerKind.warning && !!props.onClose;

    const lastPendingInvoice = last(pendingInvoices);

    const { title, body } = useMemo(() => {
      if (pendingBankConfirmation) {
        return {
          title: "Pending payment requires your approval",
          body: (
            <>
              <Body>
                Finalize your {billingContract.pendingPaymentType === "credit-purchase" ? "AI credits purchase" : "plan upgrade"} by
                approving the payment through your bank or UPI app before{" "}
                {pendingBankConfirmation.completesAt.toLocaleString(DateTime.DATE_MED)} at{" "}
                {pendingBankConfirmation.completesAt.toLocaleString(DateTime.TIME_SIMPLE)}.
              </Body>
              <Body>
                If you&apos;ve already approved the payment, we&apos;ll mark it as complete once your bank has sent the confirmation. Until
                then, we&apos;ve temporarily{" "}
                {billingContract.pendingPaymentType === "credit-purchase" ? "added AI credits" : "upgraded your plan"}.
              </Body>
            </>
          ),
        };
      }

      if (lastPendingInvoice) {
        return {
          title: "Your payment failed",
          body: (
            <>
              <Body>{lastPendingInvoice.failedPaymentMessage ?? "Your card was declined"}</Body>
              <Row>
                <Button data-testid="pending-payment-banner-failed-payment-update-payment-method" onClick={onAddPaymentMethod}>
                  Update payment method
                </Button>
              </Row>
            </>
          ),
        };
      }

      return { title: null, body: null };
    }, [billingContract.pendingPaymentType, pendingBankConfirmation, lastPendingInvoice]);

    if (!hasPendingPayments || (pendingBankConfirmation && !pendingBankConfirmation.requiresCustomerApproval)) {
      return null;
    }

    if (dismissable && pendingBankConfirmation?.acknowledged) {
      return null;
    }

    if (!title || !body) {
      return null;
    }

    return (
      <Banner
        data-testid={`pending-payment-banner-${kind === BannerKind.negative ? "failed-payment" : "pending-bank-confirmation"}`}
        onClose={dismissable ? props.onClose : undefined}
        kind={kind}
        title={title}
      >
        <Column $style={{ gap: $theme.sizing.scale300, padding: $theme.sizing.scale300 }}>{body}</Column>
      </Banner>
    );
  }
);
